/* eslint-disable max-lines-per-function */
import { ChangeEvent, ChangeEventHandler, useEffect, useRef, useState } from 'react';

import { addStyles } from '../../utils/helpers';

export interface CheckboxProps {
  /** The 'value' attribtue for the html input element */
  value: string;
  /** The label text to display next to the checkbox */
  label: string | React.ReactNode;
  /** Label for accessibility purposes */
  ariaLabel?: string;
  /** The 'name' attribute for the html input element, used for forms. */
  name?: string;
  /** Whether the label should be visible or not */
  hideLabel?: boolean;
  /** Whether input element is disabled or not */
  isDisabled?: boolean;
  /** The checkbox's checked state */
  isChecked?: boolean;
  /** Text to display below the label */
  helperText?: string;
  /** The callback that runs after checking or unchecking Checkbox */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /** An error message to display below the checkbox */
  error?: string;
  /** Allows for indeterminate state */
  isPartiallyChecked?: boolean;
}

const checkboxClasses = {
  base: 'cursor-pointer outline-none ring-offset-0 text-primary rounded-sm checked:bg-primary checked:border-primary disabled:cursor-not-allowed disabled:border-gray-500 disabled:checked:bg-gray-400',
  intermediate:
    'half-checked box-content size-0 p-1.25 mx-0.5 border-3 border-white bg-jungle-800 border-solid ring-gray-600 ring-2 hover:border-white active:border-white hover:ring-primary',
  notIntermediate: 'size-5 ring-0 active:border-primary hover:border-primary border-2 bg-white',
};

export function Checkbox({ hideLabel = false, isDisabled = false, isChecked = false, ...props }: CheckboxProps) {
  const {
    label,
    ariaLabel = typeof label === 'string' ? label : undefined,
    value,
    name,
    helperText,
    onChange,
    error,
    isPartiallyChecked,
  } = props;

  // Generate unique IDs for a11y (React v18 supports this with useId)
  const { current: fieldId } = useRef('checkbox-' + (Math.random().toString(36) + '00000000000000000').slice(2, 7));

  // Set default checked based on isChecked
  const [checked, setChecked] = useState(isChecked);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  const toggleChecked = (event: ChangeEvent<HTMLInputElement>) => {
    setChecked(!checked);
    if (onChange) {
      onChange(event);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex">
        <div className="flex items-center">
          <input
            id={fieldId}
            type="checkbox"
            className={addStyles([
              checkboxClasses.base,
              error ? 'border-red-600' : 'border-gray-600',
              isPartiallyChecked === true ? checkboxClasses.intermediate : checkboxClasses.notIntermediate,
            ])}
            value={value}
            checked={checked}
            disabled={isDisabled}
            onChange={toggleChecked}
            aria-label={hideLabel ? ariaLabel : undefined}
            name={name}
          />
        </div>
        <div className={addStyles(['ml-2', isDisabled ? 'text-gray-600' : 'text-gray-800'])}>
          {!hideLabel && (
            <label htmlFor={fieldId} className={isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}>
              {label}
            </label>
          )}
          {helperText && <p className="text-xs">{helperText}</p>}
        </div>
      </div>

      <div>{error && <p className="pt-1 text-red-600">{error}</p>}</div>
    </div>
  );
}

export default Checkbox;
